import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { OrderProcessingLine, TreeNode, TreeNodeDeclaration, TreeNodeAggregates, Admin } from '@equipmyschool/emsadminweb-models';
import { OrderProcessingComponent } from 'app/order-processing/order-processing.component';
import { AccountService, OrderProcessingService, StaffService } from 'app/server/server.module';

@Component({
  selector: 'app-orderline-panel',
  templateUrl: './orderline-panel.component.html',
  styleUrls: ['./orderline-panel.component.scss']
})
export class OrderlinePanelComponent implements OnInit, OnDestroy {
  public readonly destroy$ = new Subject<void>();
  protected readonly dialog = inject(MatDialog);
  protected readonly staffService = inject(StaffService);
  protected readonly accountService = inject(AccountService);
  protected readonly orderProcessingService = inject(OrderProcessingService);

  selectedAdmin: number | null = null;
  selectedNode: TreeNode | null = null;
  selectionChanged: Date | null = null;
  public admins$: BehaviorSubject<Admin[]> = new BehaviorSubject<Admin[]>([]);
  public title: string = "Order Lines";
  public datasource: OrderProcessingLine[] | null = null;
  public nodes: TreeNodeDeclaration[] = [
    {
      name: "All Customers",
      id: "",
      aggregates: {
        group: [
          { id: "Customer", label: "Customer" },
          { id: "AccountNo", label: "AccountNo" },
          { id: "AdminName", label: "CCS" },
          { id: "DeputyName", label: "Deputy" }
        ],
        count: true,
        sum: [
          { id: "QuotedLineValue", label: "Value" }
        ]
      },
      display: ["AccountNo", "LineCount", "Value", "CCS", "Deputy"],
      sort: ["AccountNo"],
      selectionColumn: "Customer"
    },
    {
      name: "AccountNo",
      id: "Customer",
      aggregates: {
        group: [
          { id: "BudgetID", label: "BudgetID" },
          { id: "BudgetName", label: "BudgetName" }
        ]
      } as TreeNodeAggregates,
      display: ["BudgetName"],
      sort: ["BudgetName"],
      selectionColumn: "BudgetID"
    },
    {
      name: "BudgetName",
      id: "BudgetID",
      aggregates: {
        group: [
          { id: "SalesOrder", label: "SalesOrder" },
          { id: "SOReference", label: "SOReference" },
          { id: "DeliveryAddress", label: "DeliveryAddress" },
          { id: "InvoiceAddress", label: "InvoiceAddress" },
          { id: "RequiresServicing", label: "RequiresServicing" }
        ],
        count: true
      } as TreeNodeAggregates,
      display: ["SOReference", "LineCount", "DeliveryAddress", "InvoiceAddress", "RequiresServicing"],
      sort: ["SOReference"],
      selectionColumn: "SalesOrder"
    },
    {
      name: "SOReference",
      id: "SalesOrder",
      display: [
        "Notes", "Ordered", "Received",
        "POLine", "Reference", "Description",
        "Quantity", "SupplierText",
        "StatusDescription", "ProductStatus", "FinanceStatusDesc",
        "SupplierName", "PINumber", "PIDate", "PIIsProForma",
        "EstimatedUnitPrice", "Added",
        "QuotedUnitPrice", "QuotedLineCost", "QuotedLineValue",
        "UnitPrice", "LineCost", "LineValue", "InvoiceLineValue",
        "CustomerDiscount", "LineFreightValue", "LineTaxCost", "LineTaxValue",
        "LineServicingValue", "HandlingCharge",
        "ToBeInvoiced", "Priced", "NotAvailable", "Incorrect", "Dispatched",
        "ProFormaInvoice", "Confirmed", "Cancelled",
        "RequiresServicing", "Url", "UnitType", "UnitAmount"
      ],
      sort: ["POLine"],
      selectionColumn: "LineID"
    } as TreeNodeDeclaration
  ];
  private dialogRef?: MatDialogRef<OrderProcessingComponent>;

  constructor() {
    this.admins$ = this.staffService.getAdmins().pipe(takeUntil(this.destroy$)) as BehaviorSubject<Admin[]>;
  }

  refreshData(): void {
  }

  ngOnInit(): void {
    this.admins$.pipe(takeUntil(this.destroy$)).subscribe(admins => {
      const currentUserId = this.accountService.iAmBehaviourSubject?.value?.StaffID;
      if (this.orderProcessingService.selectedAdmin === null) {
        this.orderProcessingService.selectedAdmin = admins.find(admin => admin.AdminID === currentUserId)?.AdminID ?? null;
      }
    });
    this.orderProcessingService.selectedAdmin$
      .pipe(takeUntil(this.destroy$))
      .subscribe(adminId => this.selectedAdmin = adminId);
    this.refreshData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSelectedAdminChanged(admin: number | null) {
    this.orderProcessingService.selectedAdmin = admin;
    this.datasource = null;
    if (admin != null) this.refreshData();
  }

  onSelectedNodeChanged(node: TreeNode) {
    if (this.selectedNode) this.selectedNode.selected = false;
    this.selectedNode = node;
    this.selectedNode.selected = true;
  }

  onSelectionChanged() {
    this.selectionChanged = new Date();
  }

  onEditOrders() {
    const selectedLines: number[] = this.datasource?.filter(line => line.Selected).map(line => line.LineID) || [];
    if (!selectedLines.length) {
      return;
    }

    this.dialogRef = this.dialog.open(OrderProcessingComponent, {
      data: selectedLines,
      width: '100vw',
      maxWidth: '100vw',
      height: '100vh',
      maxHeight: '100vh',
      panelClass: 'order-processing'
    });

    this.dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((result: any) => {
      this.refreshData();
    });
  }
}
